import httpClient from "../../../shared/api/httpClient";

function safeGetLocalStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function normalizeDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

export function getCurrentSessionToken() {
  return safeGetLocalStorage("sabi_access_token") || safeGetLocalStorage("access_token") || null;
}

export function getCurrentLicenseId() {
  const raw = safeGetLocalStorage("sabi_empresa_id");
  const n = Number(raw || 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function getCurrentBranchId() {
  const raw = safeGetLocalStorage("sabi_sucursal_id");
  const n = Number(raw || 0);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export async function findEmployeeByDni({ dni, licenseId }) {
  const cleanDni = normalizeDigits(dni);
  if (!cleanDni) {
    throw new Error("DNI invalido");
  }

  const targetLicenseId = Number(licenseId || 0) || getCurrentLicenseId();
  if (!targetLicenseId) {
    throw new Error("No se pudo determinar la empresa para buscar el DNI");
  }

  const query = new URLSearchParams({
    licenseId: String(targetLicenseId),
    search: cleanDni,
  });

  const response = await httpClient.get(
    `/hr/employee-shifts/employees?${query.toString()}`
  );

  const rows = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.items)
        ? response.items
        : [];

  const exactMatch = rows.find((row) => normalizeDigits(row?.dni) === cleanDni) || null;
  if (!exactMatch) {
    throw new Error("No existe un empleado con ese DNI");
  }

  const employeeId = Number(exactMatch?.id || 0);
  if (!Number.isFinite(employeeId) || employeeId <= 0) {
    throw new Error("El empleado encontrado no tiene un ID valido");
  }

  return {
    employeeId,
    fullName: String(exactMatch?.fullName || exactMatch?.full_name || "").trim(),
    dni: String(exactMatch?.dni || cleanDni),
  };
}

export async function getMe() {
  return httpClient.get("/auth/me");
}

export async function getTodaySessions({ employeeId, date, branchId }) {
  const query = new URLSearchParams({
    employeeId: String(employeeId),
    date,
    page: "1",
    limit: "20",
  });

  const targetBranchId = Number(branchId || 0) || getCurrentBranchId();
  if (targetBranchId) {
    query.set("branchId", String(targetBranchId));
  }

  return httpClient.get(
    `/hr/schedules-attendance/sessions?${query.toString()}`
  );
}

export async function getLatestSession({ employeeId, branchId }) {
  const query = new URLSearchParams({
    employeeId: String(employeeId),
    page: "1",
    limit: "1",
  });

  const targetBranchId = Number(branchId || 0) || getCurrentBranchId();
  if (targetBranchId) {
    query.set("branchId", String(targetBranchId));
  }

  const response = await httpClient.get(
    `/hr/schedules-attendance/sessions?${query.toString()}`
  );

  const sessions = Array.isArray(response?.data) ? response.data : [];
  return sessions[0] || null;
}

export async function getAttendanceDailySummary({
  date,
  licenseId,
  branchId,
  employeeId,
}) {
  const targetLicenseId = Number(licenseId || 0) || getCurrentLicenseId();
  const targetBranchId = Number(branchId || 0) || getCurrentBranchId();

  const query = new URLSearchParams();

  if (targetLicenseId) {
    query.set("licenseId", String(targetLicenseId));
  }

  if (targetBranchId) {
    query.set("branchId", String(targetBranchId));
  }

  if (employeeId) {
    query.set("employeeId", String(employeeId));
  }

  if (date) {
    query.set("date", date);
  }

  return httpClient.get(
    `/hr/schedules-attendance/daily-summary?${query.toString()}`
  );
}

export async function clockIn({ employeeId, branchId }) {
  const targetBranchId = Number(branchId || 0) || getCurrentBranchId();

  return httpClient.post("/hr/schedules-attendance/clock-in", {
    employeeId,
    branchId: targetBranchId,
    deviceId: "attendance-web",
  });
}

export async function clockOut({ employeeId, branchId }) {
  const targetBranchId = Number(branchId || 0) || getCurrentBranchId();

  return httpClient.post("/hr/schedules-attendance/clock-out", {
    employeeId,
    branchId: targetBranchId,
    deviceId: "attendance-web",
  });
}

export function resolveEmployeeId(me) {
  const fromContext = me?.context?.employeeId;
  const fromEmployee = me?.employee?.id;
  const id = Number(fromContext ?? fromEmployee ?? 0);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function getTodayYmd() {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

export function summarizeSessions(sessions) {
  const rows = Array.isArray(sessions) ? sessions : [];
  const open = rows.find((s) => s?.status === "OPEN" && !s?.clockOutAt) || null;

  const workedMinutes = rows
    .filter((s) => s?.clockInAt && s?.clockOutAt)
    .reduce((acc, s) => {
      const inMs = new Date(s.clockInAt).getTime();
      const outMs = new Date(s.clockOutAt).getTime();

      if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || outMs < inMs) {
        return acc;
      }

      return acc + Math.round((outMs - inMs) / 60000);
    }, 0);

  return {
    open,
    workedMinutes,
  };
}